// Canvas-first landing decision (ORG-BUILDER) — PURE, unit-tested.
// ─────────────────────────────────────────────────────────────────────────────
// **The canvas is the home.** A signed-in operator who opens a bare "/" lands on the
// Org canvas (/org) — a brand-new account included (T-0162). Like Figma, Miro, or
// Linear, you open Tizok INTO your workspace, not into a chat box.
//
// ## Why first-run used to be excluded, and why it no longer is
//
// The old rule bailed on `isFirstRun` for one stated reason: a new account owns no
// mesh, so there was "nothing to land the canvas on". That is no longer true. `/org`
// has a real, first-class zero-mesh surface — "No agents yet" with **+ Add agent**
// (which auto-creates the draft swarm) beside **+ Seed demo swarm**. An empty canvas
// offering the first move IS a workspace; the Concierge chat was standing in for a
// canvas that could not yet render itself. (CANVAS_HOME_DECISION, Erick: *"Primary is
// canvas … need to land on the canvas first"*.)
//
// So `isFirstRun` no longer bails — it now *grants* the landing, because a brand-new
// account has a canvas worth opening even with zero meshes.
//
// ## What is deliberately NOT changed
//
// A RETURNING operator who owns no org still lands in Messages. That is this ticket's
// scope line, not an oversight: they have spaces and DMs and no canvas of their own,
// and bouncing them to an empty canvas on every load would be a regression, not a
// homecoming. If "canvas for everyone, always" is wanted, this whole predicate
// collapses to `return true` — one word, one test.
//
// The standalone chat window STAYS (Erick: *"we can keep chat window"*). It is retired
// only as the DEFAULT landing: the persistent rail's ✉ Messages destination (`/messages`)
// is deliberately exempt from this redirect, which fires on a bare "/" alone. A first-run
// account that clicks ✉ still meets the Concierge there — the same Shell renders it.
//
// A messaging deep-link (`/?space=…`, `/?dm=…`, any query) is never hijacked.
//
// Kept a pure function of its inputs (no window, no React) so the whole decision matrix
// is testable; App.tsx wires it to `useFirstRun` + a lazy mesh count + window.location.

export interface OrgLandingInput {
  /** The first-run decision has settled (both account lists answered). */
  resolved: boolean;
  /** The account is brand-new + empty. It lands on the canvas, zero meshes and all. */
  isFirstRun: boolean;
  /** The current path — only a bare "/" is a landing candidate. */
  pathname: string;
  /** The current query string — a deep link (`?space=…`) must open messaging, not /org. */
  search: string;
  /** How many meshes the operator owns. Irrelevant at first run (the empty canvas is
   *  the landing); decisive for a returning operator. */
  meshCount: number;
}

/** Should a signed-in operator be redirected from "/" to the Org canvas (/org)? */
export function shouldLandOnOrg({
  resolved,
  isFirstRun,
  pathname,
  search,
  meshCount,
}: OrgLandingInput): boolean {
  if (!resolved) return false;
  if (pathname !== '/' || search) return false;
  // A brand-new account has a canvas worth opening (the "+ Add agent" empty state);
  // a returning one has to actually own an org.
  return isFirstRun || meshCount > 0;
}
